import { useQuery } from 'react-query';
import ls from 'local-storage';

import { useDispatchesListQuery } from 'apps/web_forms/hooks/useDispatchesListQuery';
import {
  getDispatchCategories,
  getForm,
  GET_DISPATCH_CATEGORIES,
} from 'apps/web_forms/api/api';
import { dispatchTypes } from 'apps/web_forms/utils/constants';
import { useEffect, useState } from 'react';

const CACHE_PREFIX = 'dispatchLocationInfo-';

/** Grab and integrate dispatch list data from dispatch list endpoint, dipatch categories, and
 * location info from full form xml endpoint, handling loading and error states as needed and
 * presenting a single smooth external API
 */
const useDispatches = () => {
  const {
    dispatches: rawDispatches,
    loading: dispatchesLoading,
    error: dispatchesError,
  } = useDispatchesListQuery();
  const [dispatches, setDispatches] = useState([]);
  const {
    isLoading: dispatchCategoriesLoading,
    isError: isDispatchCategoriesError,
    data: dispatchCategories,
    error: dispatchCategoriesError,
  } = useQuery(GET_DISPATCH_CATEGORIES, getDispatchCategories);

  const dispatchesInfoSansLocationLoading =
    dispatchesLoading || dispatchCategoriesLoading;
  const dispatchesInfoSansLocationIsError =
    isDispatchCategoriesError || !!dispatchesError;
  const dispatchesInfoSansLocationError = dispatchesInfoSansLocationIsError
    ? {
        dispatchesError,
        dispatchCategoriesError,
      }
    : null;

  const [dispatchesLocationInfoLoading, setDispatchesLocationInfoLoading] =
    useState(true);
  const [isDispatchesLocationInfoError, setIsDispatchesLocationInfoError] =
    useState(false);
  const [dispatchesLocationInfoError, setDispatchesLocationInfoError] =
    useState(null);

  // Grab location info for dispatch from cache - or from full form xml and cache it
  const getLocationInfoFromCacheOrServer = async (rawDispatch) => {
    const isRegular = dispatchCategories.regular.includes(
      Number(rawDispatch.identifier)
    );
    if (isRegular) {
      return { ...rawDispatch, formType: dispatchTypes.REGULAR };
    }
    const cacheId = `${CACHE_PREFIX}${rawDispatch.identifier}`;
    let cacheLocationInfo = ls.get(cacheId);
    if (cacheLocationInfo) {
      return { ...rawDispatch, ...cacheLocationInfo };
    }

    const dispatchFullXml = await getForm({ formId: rawDispatch.identifier });
    const { formType, location, address, geoFenceRadius } = dispatchFullXml;
    const dispatchLocationInfo = {
      formType,
      location,
      address,
      geoFenceRadius,
    };
    const dispatchWithLocationInfo = {
      ...rawDispatch,
      ...dispatchLocationInfo,
    };
    ls.set(cacheId, dispatchLocationInfo);
    return dispatchWithLocationInfo;
  };

  // Get location info for all dispatches (from cache or backend), and add to info from dispatch list call
  useEffect(() => {
    async function getAllLocationInfoFromCacheOrServer() {
      try {
        setDispatchesLocationInfoLoading(true);
        const dispatchesWithLocationInfoPromises = rawDispatches.map(
          (rawDispatch) => getLocationInfoFromCacheOrServer(rawDispatch)
        );
        const dispatchesWithLocationInfo = await Promise.all(
          dispatchesWithLocationInfoPromises
        );

        setDispatches(dispatchesWithLocationInfo);
        setDispatchesLocationInfoLoading(false);
      } catch (err) {
        // TODO: Integrate Bugsnag to log the errors in prod.
        // eslint-disable-next-line no-console
        console.error('Error loading dispatch location info', err);
        setIsDispatchesLocationInfoError(true);
        setDispatchesLocationInfoError(err);
        throw err;
      }
    }

    if (
      !dispatchesInfoSansLocationLoading &&
      !dispatchesInfoSansLocationError
    ) {
      getAllLocationInfoFromCacheOrServer();
    }
  }, [
    dispatchesInfoSansLocationLoading,
    dispatchesInfoSansLocationError,
    rawDispatches,
  ]);

  const isLoading =
    dispatchesInfoSansLocationLoading || dispatchesLocationInfoLoading;
  const isError =
    dispatchesInfoSansLocationIsError || isDispatchesLocationInfoError;
  const error = isError
    ? {
        ...dispatchesInfoSansLocationError,
        dispatchesLocationInfoError,
      }
    : null;

  const isReady = !isLoading && !isError;

  const regularDispatches = isReady
    ? dispatches.filter((dispatch) =>
        dispatchCategories.regular.includes(Number(dispatch.identifier))
      )
    : [];
  const geolocatedDispatches = isReady
    ? dispatches.filter((dispatch) =>
        dispatchCategories.geolocated.includes(Number(dispatch.identifier))
      )
    : [];
  const geofencedDispatches = isReady
    ? dispatches.filter((dispatch) =>
        dispatchCategories.geofenced.includes(Number(dispatch.identifier))
      )
    : [];

  return {
    isLoading,
    isError,
    error,
    dispatches,
    regularDispatches,
    geolocatedDispatches,
    geofencedDispatches,
  };
};

export default useDispatches;
export { useDispatches };
